import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Smartphone, Apple } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const copy = async (text, es) => {
  try { await navigator.clipboard.writeText(text); toast.success(es ? 'Copiado' : 'Copied'); }
  catch { toast.error(es ? 'No se pudo copiar' : 'Could not copy'); }
};

/** How to put the daily Lumera verse image on a phone home screen via an image-widget app. */
export default function WidgetPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const es = language === 'es';
  const [lang, setLang] = useState(es ? 'es' : 'en');
  const imageUrl = `${BACKEND_URL}/api/tools/votd/widget.jpg?lang=${lang}`;
  const script = `// Lumera · Verse of the Day widget
const req = new Request("${imageUrl}");
const img = await req.loadImage();
const w = new ListWidget();
w.backgroundImage = img;
w.url = "${window.location.origin}/dashboard";
w.refreshAfterDate = new Date(Date.now() + 3 * 3600 * 1000);
if (config.runsInApp) await w.presentLarge();
Script.setWidget(w);
Script.complete();`;

  const Step = ({ n, children }) => (
    <li className="flex gap-3 text-sm"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800">{n}</span><span>{children}</span></li>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8" data-testid="widget-page">
      <button type="button" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'))} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="widget-back-btn">
        <ArrowLeft className="h-4 w-4" /> {es ? 'Volver' : 'Back'}
      </button>
      <div>
        <h1 className="text-3xl font-semibold sm:text-4xl">{es ? 'Widget del Versículo del Día' : 'Verse of the Day widget'}</h1>
        <p className="mt-2 text-muted-foreground">
          {es ? 'Pon el versículo de Lumera en tu pantalla de inicio. Cambia solo cada mañana.' : "Put Lumera's verse on your home screen. It changes by itself every morning."}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[260px_1fr]">
        <div className="space-y-3">
          <img src={imageUrl} alt="Verse of the Day" className="aspect-square w-full rounded-2xl object-cover shadow-xl" data-testid="widget-preview-img" />
          <div className="flex rounded-full bg-muted p-1 text-sm">
            {['en', 'es'].map((l) => (
              <button key={l} type="button" onClick={() => setLang(l)} className={`flex-1 rounded-full py-1 ${lang === l ? 'bg-background shadow' : 'text-muted-foreground'}`} data-testid={`widget-lang-${l}`}>
                {l === 'en' ? 'English' : 'Español'}
              </button>
            ))}
          </div>
          <Button variant="outline" className="w-full" onClick={() => copy(imageUrl, es)} data-testid="widget-copy-link">
            <Copy className="mr-2 h-4 w-4" /> {es ? 'Copiar enlace de la imagen' : 'Copy image link'}
          </Button>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Apple className="h-4 w-4" /> iPhone / iPad</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-2">
                <Step n={1}>{es ? 'Instala la app gratuita "Scriptable" desde el App Store.' : 'Install the free "Scriptable" app from the App Store.'}</Step>
                <Step n={2}>{es ? 'Copia el script de abajo, abre Scriptable, toca + y pégalo. Llámalo "Lumera".' : 'Copy the script below, open Scriptable, tap + and paste it. Name it "Lumera".'}</Step>
                <Step n={3}>{es ? 'Mantén presionada la pantalla de inicio → + → Scriptable → elige el tamaño grande o mediano.' : 'Long-press your home screen → + → Scriptable → pick the large or medium size.'}</Step>
                <Step n={4}>{es ? 'Toca el widget → Script → "Lumera". ¡Listo!' : 'Tap the widget → Script → "Lumera". Done!'}</Step>
              </ol>
              <Button size="sm" className="mt-3 rounded-full" onClick={() => copy(script, es)} data-testid="widget-copy-script">
                <Copy className="mr-1.5 h-3.5 w-3.5" /> {es ? 'Copiar script' : 'Copy script'}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Smartphone className="h-4 w-4" /> Android</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-2">
                <Step n={1}>{es ? 'Instala "KWGT Kustom Widget" desde Google Play.' : 'Install "KWGT Kustom Widget" from Google Play.'}</Step>
                <Step n={2}>{es ? 'Agrega un widget KWGT a tu pantalla de inicio y tócalo para editarlo.' : 'Add a KWGT widget to your home screen and tap it to edit.'}</Step>
                <Step n={3}>{es ? 'Toca + → Imagen → Bitmap, y pega el enlace de la imagen copiado.' : 'Tap + → Image → Bitmap, and paste the copied image link.'}</Step>
                <Step n={4}>{es ? 'Guarda. KWGT actualiza la imagen automáticamente.' : 'Save. KWGT refreshes the image automatically.'}</Step>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
